/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Master Playback Transport & Timeline Controller (Task 10.5.5)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  Play,
  Pause,
  RotateCcw,
  Volume2,
  VolumeX,
  ZoomIn,
  ZoomOut,
  Repeat,
  TrendingDown,
  X,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import { useAudioMixerStore } from '../../stores/useAudioMixerStore';

export const MixerTransportControls: React.FC = () => {
  const { t } = useTranslation();
  const {
    isPlaying,
    currentTime,
    totalDuration,
    playbackRate,
    masterVolume,
    zoomLevel,
    isLooping,
    duckingSettings,
    playTimeline,
    pauseTimeline,
    stopTimeline,
    setPlaybackRate,
    setMasterVolume,
    setZoomLevel,
    toggleLoop,
    setDuckingSettings,
  } = useAudioMixerStore();

  const [showDuckingModal, setShowDuckingModal] = useState(false);

  const formatTimecode = (sec: number) => {
    const mins = Math.floor(sec / 60);
    const secs = Math.floor(sec % 60);
    const millis = Math.floor((sec % 1) * 100);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${millis
      .toString()
      .padStart(2, '0')}`;
  };

  return (
    <div className="p-3 sm:p-4 rounded-2xl bg-surface border border-surface-border shadow-md flex flex-wrap items-center justify-between gap-3">
      {/* Transport Play/Pause & Stop */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={stopTimeline}
          title={t('mixer.stopAndRewind')}
          className="size-9 rounded-xl bg-surface-elevated text-foreground-muted hover:text-foreground hover:bg-surface-border flex items-center justify-center transition-colors"
        >
          <RotateCcw className="size-4" />
        </button>

        <button
          type="button"
          onClick={() => (isPlaying ? pauseTimeline() : playTimeline())}
          className="h-9 px-4 rounded-xl bg-primary text-primary-fg hover:bg-primary/90 flex items-center gap-2 text-xs font-bold shadow-xs transition-transform active:scale-95"
        >
          {isPlaying ? (
            <>
              <Pause className="size-4" />
              <span>{t('mixer.pauseBtn')}</span>
            </>
          ) : (
            <>
              <Play className="size-4 fill-current ml-0.5" />
              <span>{t('mixer.playBtn')}</span>
            </>
          )}
        </button>

        <button
          type="button"
          onClick={toggleLoop}
          title={t('mixer.loopPlayback')}
          className={`size-9 rounded-xl flex items-center justify-center transition-colors ${
            isLooping
              ? 'bg-primary/10 text-primary border border-primary/30'
              : 'bg-surface-elevated text-foreground-muted hover:text-foreground'
          }`}
        >
          <Repeat className="size-4" />
        </button>

        {/* Timecode Digital Display */}
        <div className="px-3 py-1.5 rounded-xl bg-surface-elevated border border-surface-border font-mono text-xs flex items-center gap-1.5 text-foreground font-bold">
          <span className="text-primary">{formatTimecode(currentTime)}</span>
          <span className="text-foreground-muted font-normal">/</span>
          <span className="text-foreground-muted">{formatTimecode(totalDuration)}</span>
        </div>
      </div>

      {/* Playback Rate & Zoom Controls */}
      <div className="flex items-center gap-3">
        {/* Playback Rate */}
        <div className="flex items-center gap-1 text-xs text-foreground-muted">
          <span>{t('mixer.speedLabel')}:</span>
          <select
            value={playbackRate}
            onChange={(e) => setPlaybackRate(parseFloat(e.target.value))}
            className="px-2 py-1 rounded-lg bg-surface-elevated border border-surface-border text-xs text-foreground font-mono focus:outline-none cursor-pointer"
          >
            <option value="0.8">0.8x</option>
            <option value="1.0">1.0x</option>
            <option value="1.2">1.2x</option>
            <option value="1.5">1.5x</option>
          </select>
        </div>

        {/* Timeline Zoom */}
        <div className="flex items-center gap-1 bg-surface-elevated p-1 rounded-xl border border-surface-border">
          <button
            type="button"
            disabled={zoomLevel <= 1}
            onClick={() => setZoomLevel(Math.max(1, zoomLevel / 2))}
            title={t('mixer.zoomOut')}
            className="p-1 rounded-lg text-foreground-muted hover:text-foreground disabled:opacity-30"
          >
            <ZoomOut className="size-3.5" />
          </button>
          <span className="text-[10px] font-mono font-bold text-foreground px-1">
            {zoomLevel}x
          </span>
          <button
            type="button"
            disabled={zoomLevel >= 4}
            onClick={() => setZoomLevel(Math.min(4, zoomLevel * 2))}
            title={t('mixer.zoomIn')}
            className="p-1 rounded-lg text-foreground-muted hover:text-foreground disabled:opacity-30"
          >
            <ZoomIn className="size-3.5" />
          </button>
        </div>
      </div>

      {/* Master Volume & Ducking Settings Button */}
      <div className="flex items-center gap-3">
        {/* Master Volume */}
        <div className="flex items-center gap-2">
          {masterVolume === 0 ? (
            <VolumeX className="size-4 text-foreground-muted" />
          ) : (
            <Volume2 className="size-4 text-primary" />
          )}
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={masterVolume}
            onChange={(e) => setMasterVolume(parseFloat(e.target.value))}
            className="w-20 sm:w-24 h-1.5 accent-primary cursor-pointer"
          />
          <span className="text-xs font-mono font-bold text-foreground w-8 text-right">
            {Math.round(masterVolume * 100)}%
          </span>
        </div>

        {/* Ducking Config Button */}
        <button
          type="button"
          onClick={() => setShowDuckingModal(true)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all ${
            duckingSettings.enabled
              ? 'bg-amber-500/10 text-amber-500 border-amber-500/30'
              : 'bg-surface-elevated text-foreground-muted border-surface-border hover:text-foreground'
          }`}
        >
          <TrendingDown className="size-3.5" />
          <span>{t('mixer.duckingBtn', { percent: duckingSettings.duckingPercent })}</span>
        </button>
      </div>

      {/* Smart Ducking Settings Modal */}
      {showDuckingModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-sm bg-surface border border-surface-border rounded-2xl shadow-2xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingDown className="size-4 text-amber-500" />
                <h3 className="text-sm font-bold text-foreground">
                  {t('mixer.duckingModalTitle')}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowDuckingModal(false)}
                className="p-1 rounded-lg text-foreground-muted hover:text-foreground"
              >
                <X className="size-4" />
              </button>
            </div>

            <div className="space-y-4 text-xs">
              {/* Enable / Disable Toggle */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-surface-elevated border border-surface-border">
                <span className="font-semibold text-foreground">
                  {t('mixer.enableDuckingLabel')}
                </span>
                <input
                  type="checkbox"
                  checked={duckingSettings.enabled}
                  onChange={(e) =>
                    setDuckingSettings({ enabled: e.target.checked })
                  }
                  className="size-4 accent-primary rounded cursor-pointer"
                />
              </div>

              {/* Ducking Reduction Level */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="font-medium text-foreground-muted">
                    {t('mixer.duckingPercentLabel')}
                  </label>
                  <span className="font-mono font-bold text-foreground">
                    {duckingSettings.duckingPercent}% ({t('mixer.volumeDownTo')})
                  </span>
                </div>
                <input
                  type="range"
                  min="5"
                  max="50"
                  step="5"
                  value={duckingSettings.duckingPercent}
                  onChange={(e) =>
                    setDuckingSettings({
                      duckingPercent: parseInt(e.target.value, 10),
                    })
                  }
                  className="w-full h-1.5 accent-primary cursor-pointer"
                />
                <div className="flex justify-between text-[10px] text-foreground-muted">
                  <span>5% (Deep Duck)</span>
                  <span>20% (Broadcast Default)</span>
                  <span>50% (Subtle)</span>
                </div>
              </div>

              {/* Fade & Recovery Durations */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-medium text-foreground-muted mb-1">
                    {t('mixer.fadeDurationLabel')}
                  </label>
                  <select
                    value={duckingSettings.fadeDurationMs}
                    onChange={(e) =>
                      setDuckingSettings({
                        fadeDurationMs: parseInt(e.target.value, 10),
                      })
                    }
                    className="w-full px-2.5 py-1.5 rounded-lg bg-surface-elevated border border-surface-border text-xs text-foreground font-mono focus:outline-none cursor-pointer"
                  >
                    <option value="150">150 ms</option>
                    <option value="250">250 ms (Std)</option>
                    <option value="400">400 ms</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] font-medium text-foreground-muted mb-1">
                    {t('mixer.recoveryDurationLabel')}
                  </label>
                  <select
                    value={duckingSettings.recoveryDurationMs}
                    onChange={(e) =>
                      setDuckingSettings({
                        recoveryDurationMs: parseInt(e.target.value, 10),
                      })
                    }
                    className="w-full px-2.5 py-1.5 rounded-lg bg-surface-elevated border border-surface-border text-xs text-foreground font-mono focus:outline-none cursor-pointer"
                  >
                    <option value="250">250 ms</option>
                    <option value="400">400 ms (Std)</option>
                    <option value="600">600 ms</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="pt-2 border-t border-surface-border flex justify-end">
              <button
                type="button"
                onClick={() => setShowDuckingModal(false)}
                className="px-4 py-1.5 rounded-lg bg-primary text-primary-fg text-xs font-semibold hover:bg-primary/90 shadow-xs"
              >
                {t('cat.closeModal')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
